import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Slider } from "./ui/slider";
import { Checkbox } from "./ui/checkbox";
import {
  Calculator, RotateCcw, Snowflake, Users, Layers,
  Maximize, ArrowRight, Info, CheckCircle2, Building2, Monitor, Flame
} from "lucide-react";

const usageTypes = [
  { value: "office", label: "Office", btuLow: 130, btuHigh: 150 },
  { value: "retail", label: "Retail Outlet", btuLow: 150, btuHigh: 180 },
  { value: "restaurant", label: "Restaurant", btuLow: 180, btuHigh: 220 },
  { value: "clinic", label: "Clinic / Hospital", btuLow: 150, btuHigh: 170 },
  { value: "gym", label: "Gym / Fitness Center", btuLow: 170, btuHigh: 200 },
  { value: "server", label: "Server Room / Data Center", btuLow: 200, btuHigh: 280 },
  { value: "banquet", label: "Banquet / Event Hall", btuLow: 160, btuHigh: 200 },
  { value: "showroom", label: "Showroom", btuLow: 140, btuHigh: 170 },
];

const ceilingFactors = [
  { value: "10", label: "Up to 10 ft", factor: 1.0 },
  { value: "12", label: "10–12 ft", factor: 1.08 },
  { value: "14", label: "12–14 ft", factor: 1.15 },
  { value: "16", label: "Above 14 ft", factor: 1.25 },
];

const glassFacade = [
  { value: "none", label: "Minimal / No Glass", factor: 1.0 },
  { value: "medium", label: "Medium Glazing (30–50%)", factor: 1.10 },
  { value: "heavy", label: "Heavy Glass Façade (50%+)", factor: 1.22 },
];

const floorOptions = [
  { value: "ground", label: "Ground Floor", factor: 1.05 },
  { value: "middle", label: "Middle Floor(s)", factor: 1.0 },
  { value: "top", label: "Top Floor", factor: 1.15 },
];

const systemRecommendations = [
  { min: 0, max: 3, system: "Ductable Split / Cassette AC", scale: "Small" },
  { min: 3, max: 8, system: "VRF / Multi-Split / Cassette", scale: "Medium" },
  { min: 8, max: 20, system: "VRF System / Ductable Central", scale: "Medium-Large" },
  { min: 20, max: 50, system: "Centralized VRF / AHU-based", scale: "Large" },
  { min: 50, max: Infinity, system: "Chilled Water System / Central Plant", scale: "Enterprise" },
];

export const CommercialCalculator = ({ onGetQuote }) => {
  const [area, setArea] = useState("");
  const [ceiling, setCeiling] = useState("");
  const [usage, setUsage] = useState("");
  const [occupancy, setOccupancy] = useState([20]);
  const [glass, setGlass] = useState("");
  const [floor, setFloor] = useState("");
  const [computers, setComputers] = useState("");
  const [servers, setServers] = useState("");
  const [hasKitchen, setHasKitchen] = useState(false);
  const [freshAir, setFreshAir] = useState(false);
  const [calculated, setCalculated] = useState(false);

  const result = useMemo(() => {
    const areaVal = parseFloat(area);
    if (!areaVal || !ceiling || !usage || !glass || !floor) return null;

    const usageObj = usageTypes.find(u => u.value === usage);
    const avgBTU = ((usageObj?.btuLow || 140) + (usageObj?.btuHigh || 160)) / 2;
    const baseBTU = areaVal * avgBTU;

    const ceilingObj = ceilingFactors.find(c => c.value === ceiling);
    const afterCeiling = baseBTU * (ceilingObj?.factor || 1.0);

    const peopleBTU = occupancy[0] * 600;

    const glassObj = glassFacade.find(g => g.value === glass);
    const glassFactor = glassObj?.factor || 1.0;

    const floorObj = floorOptions.find(f => f.value === floor);
    const floorFactor = floorObj?.factor || 1.0;

    const computerBTU = (parseInt(computers) || 0) * 300;
    const serverBTU = (parseInt(servers) || 0) * 1200;

    let subtotal = (afterCeiling + peopleBTU + computerBTU + serverBTU) * glassFactor * floorFactor;

    if (hasKitchen) subtotal *= 1.12;
    if (freshAir) subtotal *= 1.05;

    const climateFactor = 1.10;
    const totalBTU = subtotal * climateFactor;
    const rawTonnage = totalBTU / 12000;

    const rec = systemRecommendations.find(r => rawTonnage >= r.min && rawTonnage < r.max)
      || systemRecommendations[systemRecommendations.length - 1];

    return {
      area: areaVal, totalBTU: Math.round(totalBTU),
      rawTonnage: rawTonnage.toFixed(1), recommendation: rec,
      inputs: {
        area: areaVal, ceilingHeight: ceiling, usageType: usage,
        occupancy: occupancy[0], glassFacade: glass, floor,
        computers: parseInt(computers) || 0, servers: parseInt(servers) || 0,
        hasKitchen, freshAir,
      },
    };
  }, [area, ceiling, usage, occupancy, glass, floor, computers, servers, hasKitchen, freshAir]);

  const handleCalculate = () => { if (result) setCalculated(true); };
  const handleReset = () => {
    setArea(""); setCeiling(""); setUsage(""); setOccupancy([20]);
    setGlass(""); setFloor(""); setComputers(""); setServers("");
    setHasKitchen(false); setFreshAir(false); setCalculated(false);
  };

  const isValid = area && ceiling && usage && glass && floor;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid lg:grid-cols-5 gap-8">
        {/* Form */}
        <div className="lg:col-span-3 bg-slate-50 rounded-2xl p-6 lg:p-8 border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="w-5 h-5 text-brand-600" />
            <h3 className="text-lg font-semibold text-slate-800">Space Details</h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Maximize className="w-3.5 h-3.5" /> Total Area (sq ft)
              </Label>
              <Input type="number" value={area} onChange={e => { setArea(e.target.value); setCalculated(false); }}
                placeholder="e.g. 2000" className="h-11 rounded-lg border-slate-200 bg-white focus:border-brand-400 focus:ring-brand-400" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">Ceiling Height</Label>
              <Select value={ceiling} onValueChange={v => { setCeiling(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select height" /></SelectTrigger>
                <SelectContent>
                  {ceilingFactors.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">Usage Type</Label>
              <Select value={usage} onValueChange={v => { setUsage(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select usage type" /></SelectTrigger>
                <SelectContent>
                  {usageTypes.map(u => <SelectItem key={u.value} value={u.value}>{u.label} ({u.btuLow}–{u.btuHigh} BTU/sq ft)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Users className="w-3.5 h-3.5" /> Occupancy: <span className="text-brand-600 text-sm font-bold">{occupancy[0]}</span>
              </Label>
              <Slider value={occupancy} onValueChange={v => { setOccupancy(v); setCalculated(false); }} min={1} max={200} step={1} className="mt-3" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>1</span><span>50</span><span>100</span><span>200</span></div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">Glass Façade Level</Label>
              <Select value={glass} onValueChange={v => { setGlass(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select glazing" /></SelectTrigger>
                <SelectContent>
                  {glassFacade.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Layers className="w-3.5 h-3.5" /> Floor Level
              </Label>
              <Select value={floor} onValueChange={v => { setFloor(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select floor" /></SelectTrigger>
                <SelectContent>
                  {floorOptions.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Equipment Load */}
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Monitor className="w-3.5 h-3.5" /> Computers / Workstations
              </Label>
              <Input type="number" value={computers} onChange={e => { setComputers(e.target.value); setCalculated(false); }}
                placeholder="e.g. 20" className="h-11 rounded-lg border-slate-200 bg-white focus:border-brand-400 focus:ring-brand-400" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Monitor className="w-3.5 h-3.5" /> Servers / Heavy Equipment
              </Label>
              <Input type="number" value={servers} onChange={e => { setServers(e.target.value); setCalculated(false); }}
                placeholder="e.g. 2" className="h-11 rounded-lg border-slate-200 bg-white focus:border-brand-400 focus:ring-brand-400" />
            </div>

            {/* Toggles */}
            <div className="sm:col-span-2 flex flex-wrap gap-6 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox id="kitchen" checked={hasKitchen} onCheckedChange={v => { setHasKitchen(v); setCalculated(false); }} />
                <label htmlFor="kitchen" className="text-sm text-slate-600 flex items-center gap-1.5 cursor-pointer">
                  <Flame className="w-3.5 h-3.5 text-accent-500" /> Kitchen / Cooking Area
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="freshair" checked={freshAir} onCheckedChange={v => { setFreshAir(v); setCalculated(false); }} />
                <label htmlFor="freshair" className="text-sm text-slate-600 cursor-pointer">Fresh Air Requirement</label>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-7">
            <Button onClick={handleCalculate} disabled={!isValid}
              className="flex-1 bg-brand-600 hover:bg-brand-700 text-white h-11 rounded-xl text-sm font-medium shadow-md shadow-brand-200 disabled:opacity-50">
              <Calculator className="w-4 h-4 mr-2" /> Estimate HVAC Load
            </Button>
            <Button onClick={handleReset} variant="outline" className="h-11 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-100 px-4">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-2">
          {calculated && result ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden h-full animate-fade-in">
              <div className="bg-brand-50 p-6 text-center">
                <Snowflake className="w-8 h-8 text-brand-600 mx-auto mb-3" />
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Estimated HVAC Capacity</p>
                <p className="text-4xl font-bold text-brand-600 mb-1">{result.rawTonnage} Ton</p>
                <p className="text-sm text-slate-600">{result.recommendation.system}</p>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Total Area</span>
                  <span className="text-sm font-semibold text-slate-800">{result.area.toLocaleString()} sq ft</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Total Heat Load</span>
                  <span className="text-sm font-semibold text-slate-800">{result.totalBTU.toLocaleString()} BTU</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Project Scale</span>
                  <span className={`text-sm font-semibold px-2 py-0.5 rounded-full ${
                    result.recommendation.scale === "Enterprise" ? "bg-violet-100 text-violet-700" :
                    result.recommendation.scale === "Large" ? "bg-blue-100 text-blue-700" :
                    result.recommendation.scale === "Medium-Large" ? "bg-indigo-100 text-indigo-700" :
                    result.recommendation.scale === "Medium" ? "bg-teal-100 text-teal-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>{result.recommendation.scale}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-xs text-slate-500">Recommended System</span>
                  <span className="text-sm font-semibold text-brand-700 text-right max-w-[60%]">{result.recommendation.system}</span>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mt-3">
                  <p className="text-xs text-amber-700 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Commercial projects require professional heat load analysis with SLD coordination. This is an indicative estimate.
                  </p>
                </div>
                <Button onClick={() => onGetQuote && onGetQuote({
                  calculatorType: "commercial",
                  inputs: result.inputs,
                  tonnage: parseFloat(result.rawTonnage),
                  system: result.recommendation.system,
                  displayTonnage: `${result.rawTonnage} Ton`,
                  scale: result.recommendation.scale,
                })} className="w-full bg-accent-500 hover:bg-accent-600 text-white h-11 rounded-xl mt-4 text-sm font-medium">
                  Get Exact HVAC Design & Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-dashed border-slate-200 h-full flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mb-5">
                <Building2 className="w-7 h-7 text-brand-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">HVAC Load Estimate</h3>
              <p className="text-sm text-slate-500 mb-6">Enter your commercial space details for a consultant-grade HVAC load estimate.</p>
              <div className="w-full space-y-2 text-left">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">What we calculate:</p>
                {["Usage-specific base load", "Occupancy & people heat", "Glass façade solar gain", "Equipment & server loads", "Kitchen & ventilation loads", "Indian climate & infiltration (+10%)"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" />
                    <span className="text-xs text-slate-500">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
